(ns yardwerkz.web.ui.components
  "Shared UI components: Button, Input, Card, Badge, Modal, Dropdown.
   Tailwind-styled, matching the TSX mockup patterns."
  (:require
   ["react" :as react]
   [uix.core :refer [defui $ use-state use-effect use-ref]]
   [yardwerkz.web.ui.icons :as icons]))

;; =============================================================================
;; Button
;; =============================================================================

(defui button
  "Primary action button.
   Variants: :primary :secondary :danger :ghost
   Sizes: :sm :md :lg"
  [{:keys [on-click children variant size disabled loading class]
    :or {variant :primary size :md}}]
  (let [base "inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        variant-cls (case variant
                      :primary "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500"
                      :secondary "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:ring-green-500"
                      :danger "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
                      :ghost "text-gray-600 hover:bg-gray-100 focus:ring-gray-500"
                      "")
        size-cls (case size
                   :sm "px-3 py-1.5 text-sm"
                   :md "px-4 py-2 text-sm"
                   :lg "px-6 py-3 text-base"
                   "")]
    ($ :button
       {:class (str base " " variant-cls " " size-cls " " class)
        :on-click on-click
        :disabled (or disabled loading)}
       (when loading
         ($ :span {:class "animate-spin mr-2"}
            ($ icons/Loader {:size 16})))
       children)))

;; =============================================================================
;; Input
;; =============================================================================

(defui input
  "Text input with label and optional error message."
  [{:keys [label value on-change placeholder type error class id
           auto-focus]
    :or {type "text"}}]
  ($ :div {:class (str "space-y-1 " class)}
     (when label
       ($ :label {:class "block text-sm font-medium text-gray-700"
                  :html-for id}
          label))
     ($ :input
        {:id id
         :class (str "w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 "
                     (if error "border-red-300" "border-gray-300"))
         :type type
         :value (or value "")
         :on-change (fn [e] (when on-change (on-change (.. e -target -value))))
         :placeholder placeholder
         :auto-focus auto-focus})
     (when error
       ($ :p {:class "text-sm text-red-600"} error))))

;; =============================================================================
;; Textarea
;; =============================================================================

(defui textarea
  [{:keys [label value on-change placeholder rows error class]
    :or {rows 3}}]
  ($ :div {:class (str "space-y-1 " class)}
     (when label
       ($ :label {:class "block text-sm font-medium text-gray-700"} label))
     ($ :textarea
        {:class (str "w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none "
                     (if error "border-red-300" "border-gray-300"))
         :value (or value "")
         :on-change (fn [e] (when on-change (on-change (.. e -target -value))))
         :placeholder placeholder
         :rows rows})
     (when error
       ($ :p {:class "text-sm text-red-600"} error))))

;; =============================================================================
;; Card
;; =============================================================================

(defui card
  "Content container with optional header."
  [{:keys [children class on-click]}]
  ($ :div
     {:class (str "bg-white rounded-xl border border-gray-200 shadow-sm "
                  (when on-click "cursor-pointer hover:border-green-300 hover:shadow-md transition-all ")
                  class)
      :on-click on-click}
     children))

;; =============================================================================
;; Badge
;; =============================================================================

(defui badge
  "Status badge with color variants."
  [{:keys [children variant class]
    :or {variant :default}}]
  (let [variant-cls (case variant
                      :success "bg-green-100 text-green-800"
                      :warning "bg-yellow-100 text-yellow-800"
                      :danger "bg-red-100 text-red-800"
                      :info "bg-blue-100 text-blue-800"
                      :default "bg-gray-100 text-gray-800"
                      "bg-gray-100 text-gray-800")]
    ($ :span
       {:class (str "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium " variant-cls " " class)}
       children)))

;; =============================================================================
;; Modal
;; =============================================================================

(defui modal
  "Overlay modal dialog with Escape key support."
  [{:keys [open on-close title children]}]
  ;; Close on Escape key
  (use-effect
   (fn []
     (when open
       (let [handler (fn [^js e]
                       (when (= (.-key e) "Escape")
                         (on-close)))]
         (.addEventListener js/document "keydown" handler)
         (fn []
           (.removeEventListener js/document "keydown" handler)))))
   [open on-close])

  (when open
    ($ :div {:class "fixed inset-0 z-50 overflow-y-auto"}
       ;; Backdrop
       ($ :div {:class "fixed inset-0 bg-black/50 transition-opacity"
                :on-click on-close})
       ;; Dialog
       ($ :div {:class "flex min-h-full items-center justify-center p-4"}
          ($ :div {:class "relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6 z-10"}
             ;; Header
             ($ :div {:class "flex items-center justify-between mb-4"}
                ($ :h3 {:class "text-lg font-semibold text-gray-900"} title)
                ($ :button {:class "text-gray-400 hover:text-gray-600"
                            :on-click on-close}
                   ($ icons/X {:size 20})))
             ;; Content
             children)))))

;; =============================================================================
;; Spinner
;; =============================================================================

(defui spinner
  "Loading spinner."
  [{:keys [size class]
    :or {size :md}}]
  (let [size-cls (case size
                   :sm "h-4 w-4"
                   :md "h-8 w-8"
                   :lg "h-12 w-12"
                   "h-8 w-8")]
    ($ :div {:class (str "animate-spin rounded-full border-b-2 border-green-600 " size-cls " " class)})))

;; =============================================================================
;; Empty State
;; =============================================================================

(defui empty-state
  [{:keys [icon title description action]}]
  ($ :div {:class "text-center py-12"}
     (when icon
       ($ :div {:class "mx-auto h-12 w-12 text-gray-400 mb-4"} icon))
     ($ :h3 {:class "text-sm font-medium text-gray-900 mb-1"} title)
     (when description
       ($ :p {:class "text-sm text-gray-500 mb-4"} description))
     (when action action)))

;; =============================================================================
;; Tab Bar
;; =============================================================================

(defui tabs
  "Horizontal tab selector."
  [{:keys [tabs active on-change]}]
  ($ :div {:class "flex border-b border-gray-200"}
     (for [{:keys [id label]} tabs]
       ($ :button
          {:key id
           :class (str "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors "
                       (if (= active id)
                         "border-green-600 text-green-600"
                         "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"))
           :on-click #(on-change id)}
          label))))

;; =============================================================================
;; Toast (simple notification)
;; =============================================================================

(defonce toast-state (atom nil))

(defn show-toast!
  "Show a temporary toast notification.
   type: :success :error :info"
  [type message]
  (reset! toast-state {:type type :message message})
  (js/setTimeout #(reset! toast-state nil) 3000))

(defui toast-container []
  (let [[toast set-toast] (use-state nil)]
    (use-effect
     (fn []
       (let [watcher (add-watch toast-state ::toast-watcher
                                (fn [_ _ _ new-val]
                                  (set-toast new-val)))]
         (fn []
           (remove-watch toast-state ::toast-watcher))))
     [])
    (when toast
      (let [type-cls (case (:type toast)
                       :success "bg-green-600"
                       :error "bg-red-600"
                       :info "bg-blue-600"
                       "bg-gray-600")]
        ($ :div {:class (str "fixed bottom-4 right-4 z-50 px-4 py-3 rounded-lg text-white text-sm shadow-lg " type-cls)}
           (:message toast))))))

;; =============================================================================
;; Loading Skeleton
;; =============================================================================

(defui skeleton
  "Animated loading placeholder."
  [{:keys [class width height]}]
  ($ :div {:class (str "animate-pulse bg-gray-200 rounded "
                       class)
           :style (cond-> {}
                    width (assoc :width width)
                    height (assoc :height height))}))

(defui skeleton-card
  "Card-shaped loading skeleton."
  []
  ($ :div {:class "bg-white rounded-xl border border-gray-200 p-4 space-y-3"}
     ($ :div {:class "flex items-center justify-between"}
        ($ skeleton {:class "h-5 w-32"})
        ($ skeleton {:class "h-5 w-20 rounded-full"}))
     ($ skeleton {:class "h-4 w-48"})
     ($ skeleton {:class "h-4 w-36"})
     ($ :div {:class "flex items-center justify-between pt-2 border-t border-gray-100 mt-2"}
        ($ skeleton {:class "h-6 w-16"})
        ($ skeleton {:class "h-4 w-24"}))))

(defui skeleton-list
  "Multiple skeleton cards for list loading states."
  [{:keys [count] :or {count 3}}]
  ($ :div {:class "space-y-4"}
     (for [i (range count)]
       ($ skeleton-card {:key i}))))

;; =============================================================================
;; Error Boundary
;; =============================================================================

(def error-boundary-class
  "ES6 class component for error boundary (hooks can't catch render errors)."
  (let [cls (fn ErrorBoundary [props]
              (this-as ^js this
                       (.call react/Component this props)
                       (set! (.-state this) #js {:hasError false :error nil})
                       this))]
    (set! (.-prototype cls) (js/Object.create (.-prototype react/Component)))
    (set! (.. cls -prototype -constructor) cls)
    (set! (.. cls -prototype -render)
          (fn []
            (this-as ^js this
                     (if (.. this -state -hasError)
                       (.createElement react "div"
                                       #js {:className "max-w-lg mx-auto py-16 px-4 text-center"}
                                       (.createElement react "div"
                                                       #js {:className "mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4"}
                                                       (.createElement react "span"
                                                                       #js {:className "text-2xl"} "!"))
                                       (.createElement react "h2"
                                                       #js {:className "text-xl font-bold text-gray-900 mb-2"}
                                                       "Something went wrong")
                                       (.createElement react "p"
                                                       #js {:className "text-gray-500 mb-6"}
                                                       (or (some-> (.. this -state -error) .-message)
                                                           "An unexpected error occurred."))
                                       (.createElement react "button"
                                                       #js {:className "px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                                            :onClick (fn []
                                                                       (.setState this #js {:hasError false :error nil}))}
                                                       "Try Again"))
                       (.. this -props -children)))))
    (set! (.-getDerivedStateFromError cls)
          (fn [error]
            #js {:hasError true :error error}))
    cls))

(defui error-boundary [{:keys [children]}]
  (.createElement react error-boundary-class nil children))
